import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { TentTypeV1, TentTypeV2 } from '@camp67/model';
import { displayPrice } from '../../state/prices';
import { getOfferingPrice, getOfferingCapacity } from '../../state/offerings';
import { Tooltip } from '../tooltip';
import { trackButtonClick, ButtonTag } from '../../state/analytics';

export type Operation = 'add' | 'subtract';

/**
 * Gallery image with optional portrait flag
 */
export interface GalleryImage {
  imageUrl: string;
  /** If true, image is portrait orientation and should use object-fit: contain */
  portrait?: boolean;
}

export interface TentGalleryItem {
  tentType: TentTypeV1 | TentTypeV2;
  title: string;
  displayName?: React.ReactNode;
  description: string;
  imageAlt: string;
  gallery: GalleryImage[];
  amenities: Array<{ summary: string; detail?: React.ReactNode }>;
  /** Whether this tent has a private bathroom (true) or shared bathroom (false) */
  hasPrivateBathroom?: boolean;
}

export interface TentExperienceGalleryItemMessages {
  pricePerNight: string;
  mealsIncluded: string;
  privateBathroom: string;
  sharedBathroom: string;
  galleryOf: string; // e.g., "Image {{current}} of {{total}}"
  addTooltip: string;
  subtractTooltip: string;
}

export interface TentExperienceGalleryItemProps {
  tent: TentGalleryItem;
  onClick?: (type: TentTypeV1 | TentTypeV2, operation: Operation) => void;
  currentCount?: number;
  messages: TentExperienceGalleryItemMessages;
  /** Optional: hide prices (e.g., in inquiry mode) */
  hidePrices?: boolean;
}

export const TentExperienceGalleryItem: React.FC<TentExperienceGalleryItemProps> = ({
  tent,
  onClick,
  currentCount = 0,
  messages,
  hidePrices = false,
}) => {
  const {
    tentType,
    title,
    displayName,
    description,
    imageAlt,
    gallery,
    amenities,
    hasPrivateBathroom = false,
  } = tent;

  const [imageIndex, setImageIndex] = useState(0);

  // Reset image index when tent changes
  useEffect(() => {
    setImageIndex(0);
  }, [tentType]);

  // Get price and capacity from offerings
  const basePrice = getOfferingPrice(tentType) ?? 0;
  const capacity = getOfferingCapacity(tentType) ?? 0;
  const price = displayPrice({ priceInINR: basePrice, showCurrency: true });
  const guestsText = capacity === 1 ? '1 guest' : `${capacity} guests`;

  const onClickAdd = onClick ? () => onClick(tentType, 'add') : null;
  const onClickSubtract = onClick ? () => onClick(tentType, 'subtract') : null;

  const onClickGalleryNext = useCallback(() => {
    setImageIndex((prev) => (prev + 1) % gallery.length);
    trackButtonClick(ButtonTag.NEXT_IMAGE);
  }, [gallery.length]);

  const onClickGalleryPrev = useCallback(() => {
    setImageIndex((prev) => (prev === 0 ? gallery.length - 1 : prev - 1));
    trackButtonClick(ButtonTag.PREV_IMAGE);
  }, [gallery.length]);

  const currentImage = gallery[imageIndex];
  const imageUrl = currentImage?.imageUrl;
  const isPortrait = currentImage?.portrait === true;

  // Format gallery counter (e.g., "Image 1 of 3")
  const galleryCounter = messages.galleryOf
    .replace('{{current}}', String(imageIndex + 1))
    .replace('{{total}}', String(gallery.length));

  return (
    <div
      className="w-full h-[95%] z-20 relative m-auto flex flex-col items-center justify-start rounded-2xl mt-1"
      id="tent-experience-modal-content"
    >
      {/* Gallery image - fixed height to prevent jumping on mobile */}
      <div
        className={clsx(
          'w-full relative h-72 sm:h-96 md:h-[480px]',
          isPortrait && 'bg-gray-100',
        )}
      >
        <img
          alt={imageAlt}
          loading="lazy"
          src={imageUrl}
          className={clsx(
            'w-full h-full pointer-events-none select-none',
            isPortrait ? 'object-contain' : 'object-cover',
          )}
        />
        <div className="bg-blue-50 w-full absolute bottom-0 flex items-center justify-center px-5 gap-4">
          <button onClick={onClickGalleryPrev} type="button" className="text-black">
            &larr;
          </button>
          <p className="text-black text-center py-3">{galleryCounter}</p>
          <button onClick={onClickGalleryNext} type="button" className="text-black">
            &rarr;
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col gap-3 w-full items-start">
        <hgroup className="w-full">
          <div className="flex flex-row items-center justify-between px-6 py-3 border-b border-b-orange-100 border-dotted">
            <div className="block text-left font-serif text-md text-black font-bold text-nowrap whitespace-nowrap w-full">
              <span className="flex justify-between w-full flex-wrap">
                <span>{displayName || title}</span>
                {!hidePrices && (
                  <span className="font-medium text-right">
                    {price} {messages.pricePerNight}
                  </span>
                )}
              </span>
              <span className="flex flex-row flex-wrap font-sans items-start mt-0.5 justify-between gap-1.5">
                <span className="flex flex-wrap">
                  <span className="flex mr-1 flex-row gap-1 items-center text-xs opacity-50 font-normal">
                    <GuestIcon />
                    {guestsText}
                  </span>
                  <span className="flex flex-row gap-1 items-center text-xs opacity-50 font-normal mr-1">
                    <MealsIcon />
                    {messages.mealsIncluded}
                  </span>
                </span>
              </span>
              <span className="flex font-sans flex-row gap-1 items-center text-xs opacity-50 font-normal">
                <BathroomIcon />
                {hasPrivateBathroom ? messages.privateBathroom : messages.sharedBathroom}
              </span>
            </div>
          </div>
        </hgroup>

        <div className="px-6 flex flex-col gap-3 w-full">
          {/* Description and add/subtract buttons */}
          <div className="flex flex-col sm:flex-row gap-y-2 sm:gap-y-0 justify-between">
            <p className="font-sans text-md font-normal text-left max-w-100">
              {description}
            </p>
            {onClickAdd && onClickSubtract ? (
              <div className="flex items-center gap-2">
                <span className="text-xs mr-2 text-center">{currentCount}</span>
                <Tooltip text={`${messages.subtractTooltip} ${title}`}>
                  <button
                    type="button"
                    className={clsx(
                      'w-10 h-10 bg-[#F9F9F9] hover:bg-[#F1F1F1] rounded-sm transition-all',
                    )}
                    onClick={onClickSubtract}
                  >
                    -
                  </button>
                </Tooltip>
                <Tooltip text={`${messages.addTooltip} ${title}`}>
                  <button
                    type="button"
                    className={clsx(
                      'w-10 h-10 bg-green-150 hover:bg-green-200 rounded-sm transition-all',
                    )}
                    onClick={onClickAdd}
                  >
                    +
                  </button>
                </Tooltip>
              </div>
            ) : null}
          </div>

          {/* Amenities */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {amenities.map((amenity, index) => (
              <AmenityCompact
                key={index}
                summary={amenity.summary}
                detail={amenity.detail}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/** Collapsible amenity display component (matches original camp67 AmenityCompact) */
const AmenityCompact: React.FC<{ summary: string; detail?: React.ReactNode }> = ({
  summary,
  detail,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOpen = useCallback(() => setIsOpen((prev) => !prev), []);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <details
      className={clsx(
        'flex flex-col text-left w-full rounded-md px-4 py-1.5 select-none list-none cursor-pointer bg-gray-100 hover:bg-orange-100 transition-all duration-500 ease-in-out',
      )}
      open={isOpen}
      id={`collapse-${summary}`}
      onClick={(e) => {
        e.preventDefault();
        toggleOpen();
      }}
    >
      <summary
        className={clsx(
          'font-sans uppercase tracking-wider font-medium flex flex-row items-center justify-between text-xs leading-1 xl:leading-8',
        )}
      >
        <p className="m-0 flex leading-6">{summary}</p>
        <svg
          width="56"
          height="16"
          viewBox="0 0 56 30"
          fill="none"
          stroke="currentColor"
          strokeWidth="6"
          className={clsx('w-4 stroke-black cursor-pointer transition-transform', {
            '-rotate-180': isOpen,
          })}
        >
          <polyline points="8 8 28 24 48 8" />
        </svg>
      </summary>
      <div className={clsx('mt-0 pb-4')}>{detail}</div>
    </details>
  );
};

/** Simple icon components */
const GuestIcon: React.FC = () => (
  <svg className="w-3.5 min-w-3.5" viewBox="0 0 20 20" fill="currentColor">
    <path d="M10 10a4 4 0 100-8 4 4 0 000 8zm0 2c-4 0-8 2-8 4v2h16v-2c0-2-4-4-8-4z" />
  </svg>
);

const MealsIcon: React.FC = () => (
  <svg className="w-3.5 min-w-3.5" viewBox="0 0 14 13" fill="currentColor">
    <path d="M7 0C3.13 0 0 2.91 0 6.5S3.13 13 7 13s7-2.91 7-6.5S10.87 0 7 0zm0 12c-3.31 0-6-2.46-6-5.5S3.69 1 7 1s6 2.46 6 5.5S10.31 12 7 12z" />
  </svg>
);

const BathroomIcon: React.FC = () => (
  <svg className="w-3.5 min-w-3.5" viewBox="0 0 14 13" fill="currentColor">
    <path d="M12 5H2V4h10v1zm0 6H2v-1h10v1zm1-8H1a1 1 0 00-1 1v7a1 1 0 001 1h12a1 1 0 001-1V4a1 1 0 00-1-1z" />
  </svg>
);
